import { fitInto } from './receiptLogoBox';

/**
 * The location logo as ThermalDocument.image() prints it: 1-bit raster rows
 * as wide as the paper, MSB = leftmost dot, 1 = black, base64 encoded. The
 * native and web loaders both end here, so a logo dithers the same on every
 * platform and transport.
 */
export type ReceiptLogo = {
  base64: string;
  widthDots: number;
  heightDots: number;
};

/** The logo fits in two thirds of the paper width and 160 dots (20 mm). */
export const MAX_LOGO_HEIGHT_DOTS = 160;

/** The box the logo is scaled into, for paper `widthDots` wide. */
export function logoBox(widthDots: number): { width: number; height: number } {
  return {
    width: Math.max(1, Math.floor((widthDots * 2) / 3)),
    height: MAX_LOGO_HEIGHT_DOTS,
  };
}

/**
 * Scale an image to fit the logo box, keeping its aspect ratio; small logos
 * scale up (what Cloudinary's c_fit does, so native and web agree).
 */
export function fitLogo(
  naturalWidth: number,
  naturalHeight: number,
  widthDots: number,
): { width: number; height: number } {
  const box = logoBox(widthDots);
  return fitInto(naturalWidth, naturalHeight, box.width, box.height);
}

/**
 * `gray` holds one byte per pixel (0 black … 255 white) of the fitted logo,
 * `width` × `height`. It is centered on white across the paper (centered in
 * the pixels: cheap printers ignore alignment commands), then turned black
 * and white with Floyd–Steinberg dithering so light colors print as a dot
 * pattern instead of disappearing.
 */
export function rasterizeLogo(
  gray: Uint8Array,
  width: number,
  height: number,
  widthDots: number,
): ReceiptLogo {
  if (gray.length < width * height) {
    throw new Error('gray buffer is shorter than width × height');
  }
  const left = Math.max(0, Math.floor((widthDots - width) / 2));
  const cols = Math.min(width, widthDots - left);
  const paper = new Float32Array(widthDots * height).fill(255);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < cols; x++) {
      paper[y * widthDots + left + x] = gray[y * width + x];
    }
  }

  const rowBytes = Math.ceil(widthDots / 8);
  const bits = new Uint8Array(rowBytes * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < widthDots; x++) {
      const i = y * widthDots + x;
      const black = paper[i] < 128;
      if (black) bits[y * rowBytes + (x >> 3)] |= 0x80 >> (x & 7);
      const err = paper[i] - (black ? 0 : 255);
      if (x + 1 < widthDots) paper[i + 1] += (err * 7) / 16;
      if (y + 1 < height) {
        if (x > 0) paper[i + widthDots - 1] += (err * 3) / 16;
        paper[i + widthDots] += (err * 5) / 16;
        if (x + 1 < widthDots) paper[i + widthDots + 1] += err / 16;
      }
    }
  }
  return { base64: bytesToBase64(bits), widthDots, heightDots: height };
}

/**
 * Base64 of raw bytes. btoa exists on Hermes, in browsers and in Node; the
 * input is chunked so fromCharCode never sees an oversized argument list.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x2000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(
      ...Array.from(bytes.subarray(i, i + CHUNK)),
    );
  }
  return btoa(binary);
}
